/**
 * The instrument master.
 *
 * Every cash figure in AEGIS is computed through this table. A wrong tick value
 * does not throw — it produces a plausible, wrong number for every trade in that
 * contract, forever.
 *
 * The identity `tickValue = tickSize * pointValue` is asserted for every row at
 * module load. A bad row fails the process at startup rather than corrupting a
 * P&L six months later.
 *
 * These figures are transcribed from CME contract specifications and MUST be
 * re-verified against the exchange before production launch. They are recorded
 * as data so that adding or correcting a contract is a data change.
 *
 * @see docs/16-instrument-reference.md
 */
import { dec, type Decimal } from '../money.js';
import type { AssetClass, InstrumentKind, Instrument, PriceFormat, RollRule, SettlementType, VenueId } from './types.js';
import { isKnownVenue } from './venues.js';
import { SESSION_TEMPLATES } from './sessions.js';
import { FX_PAIRS } from './fx.gen.js';

/** Futures month codes. */
export const MONTH_CODES: Readonly<Record<string, number>> = {
  F: 1, G: 2, H: 3, J: 4, K: 5, M: 6, N: 7, Q: 8, U: 9, V: 10, X: 11, Z: 12,
};
export const CODE_FOR_MONTH: readonly string[] =
  ['', 'F', 'G', 'H', 'J', 'K', 'M', 'N', 'Q', 'U', 'V', 'X', 'Z'];

const QUARTERLY = ['H', 'M', 'U', 'Z'] as const;
const ALL_MONTHS = ['F', 'G', 'H', 'J', 'K', 'M', 'N', 'Q', 'U', 'V', 'X', 'Z'] as const;

/**
 * A row of the instrument master, before validation turns it into an
 * `Instrument`. Exported so the generated FX rows can be typed against the same
 * shape rather than a parallel one that could drift from it.
 */
export interface Spec {
  root: string;
  exchange: VenueId;
  /** Defaults to 'future' — every seeded row below one is a futures contract. */
  kind?: InstrumentKind;
  /** Defaults to 1. Fractional only for crypto. */
  quantityStep?: string;
  /** Spot FX only. */
  base?: string;
  quote?: string;
  contractSize?: string;
  assetClass: AssetClass;
  description: string;
  tickSize: string;
  tickValue: string;
  pointValue: string;
  priceFormat?: PriceFormat;
  displayPrecision: number;
  parentRoot?: string;
  sizeRatio?: string;
  /** Futures only. Omitted for every other kind rather than emptied. */
  contractMonths?: readonly string[];
  settlementType?: SettlementType;
  rollRule?: RollRule;
  sessionTemplateId: string;
  currency?: string;
}

const SPECS: readonly Spec[] = [
  // --- equity index --------------------------------------------------------
  { root: 'ES', exchange: 'CME', assetClass: 'equity_index', description: 'E-mini S&P 500',
    tickSize: '0.25', tickValue: '12.50', pointValue: '50', displayPrecision: 2,
    contractMonths: QUARTERLY, settlementType: 'cash', sessionTemplateId: 'cme_equity_index' },
  { root: 'MES', exchange: 'CME', assetClass: 'equity_index', description: 'Micro E-mini S&P 500',
    tickSize: '0.25', tickValue: '1.25', pointValue: '5', displayPrecision: 2,
    parentRoot: 'ES', sizeRatio: '0.1',
    contractMonths: QUARTERLY, settlementType: 'cash', sessionTemplateId: 'cme_equity_index' },
  { root: 'NQ', exchange: 'CME', assetClass: 'equity_index', description: 'E-mini Nasdaq-100',
    tickSize: '0.25', tickValue: '5.00', pointValue: '20', displayPrecision: 2,
    contractMonths: QUARTERLY, settlementType: 'cash', sessionTemplateId: 'cme_equity_index' },
  { root: 'MNQ', exchange: 'CME', assetClass: 'equity_index', description: 'Micro E-mini Nasdaq-100',
    tickSize: '0.25', tickValue: '0.50', pointValue: '2', displayPrecision: 2,
    parentRoot: 'NQ', sizeRatio: '0.1',
    contractMonths: QUARTERLY, settlementType: 'cash', sessionTemplateId: 'cme_equity_index' },
  { root: 'YM', exchange: 'CBOT', assetClass: 'equity_index', description: 'E-mini Dow ($5)',
    tickSize: '1', tickValue: '5.00', pointValue: '5', displayPrecision: 0,
    contractMonths: QUARTERLY, settlementType: 'cash', sessionTemplateId: 'cme_equity_index' },
  { root: 'MYM', exchange: 'CBOT', assetClass: 'equity_index', description: 'Micro E-mini Dow',
    tickSize: '1', tickValue: '0.50', pointValue: '0.5', displayPrecision: 0,
    parentRoot: 'YM', sizeRatio: '0.1',
    contractMonths: QUARTERLY, settlementType: 'cash', sessionTemplateId: 'cme_equity_index' },
  { root: 'RTY', exchange: 'CME', assetClass: 'equity_index', description: 'E-mini Russell 2000',
    tickSize: '0.1', tickValue: '5.00', pointValue: '50', displayPrecision: 1,
    contractMonths: QUARTERLY, settlementType: 'cash', sessionTemplateId: 'cme_equity_index' },
  { root: 'M2K', exchange: 'CME', assetClass: 'equity_index', description: 'Micro E-mini Russell 2000',
    tickSize: '0.1', tickValue: '0.50', pointValue: '5', displayPrecision: 1,
    parentRoot: 'RTY', sizeRatio: '0.1',
    contractMonths: QUARTERLY, settlementType: 'cash', sessionTemplateId: 'cme_equity_index' },

  // --- energy --------------------------------------------------------------
  { root: 'CL', exchange: 'NYMEX', assetClass: 'energy', description: 'Crude Oil (WTI)',
    tickSize: '0.01', tickValue: '10.00', pointValue: '1000', displayPrecision: 2,
    contractMonths: ALL_MONTHS, settlementType: 'physical', sessionTemplateId: 'cme_energy_metals' },
  { root: 'MCL', exchange: 'NYMEX', assetClass: 'energy', description: 'Micro Crude Oil',
    tickSize: '0.01', tickValue: '1.00', pointValue: '100', displayPrecision: 2,
    parentRoot: 'CL', sizeRatio: '0.1',
    contractMonths: ALL_MONTHS, settlementType: 'cash', sessionTemplateId: 'cme_energy_metals' },
  { root: 'NG', exchange: 'NYMEX', assetClass: 'energy', description: 'Natural Gas (Henry Hub)',
    tickSize: '0.001', tickValue: '10.00', pointValue: '10000', displayPrecision: 3,
    contractMonths: ALL_MONTHS, settlementType: 'physical', sessionTemplateId: 'cme_energy_metals' },

  // --- metals --------------------------------------------------------------
  { root: 'GC', exchange: 'COMEX', assetClass: 'metal', description: 'Gold',
    tickSize: '0.1', tickValue: '10.00', pointValue: '100', displayPrecision: 1,
    contractMonths: ['G', 'J', 'M', 'Q', 'V', 'Z'], settlementType: 'physical',
    sessionTemplateId: 'cme_energy_metals' },
  { root: 'MGC', exchange: 'COMEX', assetClass: 'metal', description: 'Micro Gold',
    tickSize: '0.1', tickValue: '1.00', pointValue: '10', displayPrecision: 1,
    parentRoot: 'GC', sizeRatio: '0.1',
    contractMonths: ['G', 'J', 'M', 'Q', 'V', 'Z'], settlementType: 'cash',
    sessionTemplateId: 'cme_energy_metals' },
  { root: 'SI', exchange: 'COMEX', assetClass: 'metal', description: 'Silver',
    tickSize: '0.005', tickValue: '25.00', pointValue: '5000', displayPrecision: 3,
    contractMonths: ['H', 'K', 'N', 'U', 'Z'], settlementType: 'physical',
    sessionTemplateId: 'cme_energy_metals' },

  // --- rates ---------------------------------------------------------------
  // ZN's tick value of 15.625 is the canonical demonstration of why cash is
  // Decimal and never float.
  { root: 'ZB', exchange: 'CBOT', assetClass: 'rate', description: '30-Year U.S. Treasury Bond',
    tickSize: '0.03125', tickValue: '31.25', pointValue: '1000',
    priceFormat: 'fractional_32', displayPrecision: 5,
    contractMonths: QUARTERLY, settlementType: 'physical', sessionTemplateId: 'cbot_rates' },
  { root: 'ZN', exchange: 'CBOT', assetClass: 'rate', description: '10-Year U.S. Treasury Note',
    tickSize: '0.015625', tickValue: '15.625', pointValue: '1000',
    priceFormat: 'fractional_64', displayPrecision: 6,
    contractMonths: QUARTERLY, settlementType: 'physical', sessionTemplateId: 'cbot_rates' },

  // --- fx ------------------------------------------------------------------
  { root: '6E', exchange: 'CME', assetClass: 'fx', description: 'Euro FX',
    tickSize: '0.00005', tickValue: '6.25', pointValue: '125000', displayPrecision: 5,
    contractMonths: QUARTERLY, settlementType: 'physical', sessionTemplateId: 'cme_fx' },
  { root: '6J', exchange: 'CME', assetClass: 'fx', description: 'Japanese Yen',
    tickSize: '0.0000005', tickValue: '6.25', pointValue: '12500000', displayPrecision: 7,
    contractMonths: QUARTERLY, settlementType: 'physical', sessionTemplateId: 'cme_fx' },
  // --- equities ------------------------------------------------------------
  // A share is the degenerate case of the tick identity: one point of price is
  // one dollar, so pointValue is 1 and tickValue equals tickSize. The identity
  // still holds, which is why shares are not exempted from it.
  { root: 'AAPL', exchange: 'NASDAQ', kind: 'stock', assetClass: 'equity', description: 'Apple Inc.',
    tickSize: '0.01', tickValue: '0.01', pointValue: '1', displayPrecision: 2,
    sessionTemplateId: 'us_equity' },
  { root: 'NVDA', exchange: 'NASDAQ', kind: 'stock', assetClass: 'equity', description: 'NVIDIA Corporation',
    tickSize: '0.01', tickValue: '0.01', pointValue: '1', displayPrecision: 2,
    sessionTemplateId: 'us_equity' },
  { root: 'SPY', exchange: 'ARCA', kind: 'stock', assetClass: 'equity', description: 'SPDR S&P 500 ETF Trust',
    tickSize: '0.01', tickValue: '0.01', pointValue: '1', displayPrecision: 2,
    sessionTemplateId: 'us_equity' },

  // --- crypto --------------------------------------------------------------
  // The kind that forces quantity off integers. 0.0137 BTC is an ordinary
  // position, and rounding it to 0 loses the trade while rounding to 1 misprices
  // it by about seventy times.
  { root: 'BTCUSD', exchange: 'COINBASE', kind: 'crypto_spot', assetClass: 'crypto',
    description: 'Bitcoin / US Dollar', quantityStep: '0.00000001',
    tickSize: '0.01', tickValue: '0.01', pointValue: '1', displayPrecision: 2,
    sessionTemplateId: 'always_open' },
  { root: 'ETHUSD', exchange: 'COINBASE', kind: 'crypto_spot', assetClass: 'crypto',
    description: 'Ether / US Dollar', quantityStep: '0.00000001',
    tickSize: '0.01', tickValue: '0.01', pointValue: '1', displayPrecision: 2,
    sessionTemplateId: 'always_open' },
  { root: 'BTCUSDT.P', exchange: 'BINANCE', kind: 'crypto_perp', assetClass: 'crypto',
    description: 'Bitcoin / USDT perpetual', quantityStep: '0.001',
    tickSize: '0.1', tickValue: '0.1', pointValue: '1', displayPrecision: 1,
    sessionTemplateId: 'always_open' },


  // --- rates, the rest of the ladder ---------------------------------------
  // A trader who spreads ZT against ZN needs both, and the missing legs were
  // the difference between "AEGIS covers rates" and "AEGIS covers two rates".
  { root: 'ZT', exchange: 'CBOT', assetClass: 'rate', description: '2-Year U.S. Treasury Note',
    tickSize: '0.0078125', tickValue: '15.625', pointValue: '2000',
    priceFormat: 'fractional_64', displayPrecision: 7,
    contractMonths: QUARTERLY, settlementType: 'physical', sessionTemplateId: 'cbot_rates' },
  { root: 'ZF', exchange: 'CBOT', assetClass: 'rate', description: '5-Year U.S. Treasury Note',
    tickSize: '0.0078125', tickValue: '7.8125', pointValue: '1000',
    priceFormat: 'fractional_64', displayPrecision: 7,
    contractMonths: QUARTERLY, settlementType: 'physical', sessionTemplateId: 'cbot_rates' },
  { root: 'TN', exchange: 'CBOT', assetClass: 'rate', description: 'Ultra 10-Year U.S. Treasury Note',
    tickSize: '0.015625', tickValue: '15.625', pointValue: '1000',
    priceFormat: 'fractional_64', displayPrecision: 6,
    contractMonths: QUARTERLY, settlementType: 'physical', sessionTemplateId: 'cbot_rates' },
  { root: 'UB', exchange: 'CBOT', assetClass: 'rate', description: 'Ultra U.S. Treasury Bond',
    tickSize: '0.03125', tickValue: '31.25', pointValue: '1000',
    priceFormat: 'fractional_32', displayPrecision: 5,
    contractMonths: QUARTERLY, settlementType: 'physical', sessionTemplateId: 'cbot_rates' },
  // Decimal-priced, unlike the notes above — SOFR quotes as 100 minus the rate.
  { root: 'SR3', exchange: 'CME', assetClass: 'rate', description: '3-Month SOFR',
    tickSize: '0.005', tickValue: '12.50', pointValue: '2500', displayPrecision: 4,
    contractMonths: QUARTERLY, settlementType: 'cash', sessionTemplateId: 'cbot_rates' },

  // --- energy, refined and micro -------------------------------------------
  { root: 'RB', exchange: 'NYMEX', assetClass: 'energy', description: 'RBOB Gasoline',
    tickSize: '0.0001', tickValue: '4.20', pointValue: '42000', displayPrecision: 4,
    contractMonths: ALL_MONTHS, settlementType: 'physical', sessionTemplateId: 'cme_energy_metals' },
  { root: 'HO', exchange: 'NYMEX', assetClass: 'energy', description: 'NY Harbor ULSD',
    tickSize: '0.0001', tickValue: '4.20', pointValue: '42000', displayPrecision: 4,
    contractMonths: ALL_MONTHS, settlementType: 'physical', sessionTemplateId: 'cme_energy_metals' },
  { root: 'BZ', exchange: 'NYMEX', assetClass: 'energy', description: 'Brent Crude (Last Day)',
    tickSize: '0.01', tickValue: '10.00', pointValue: '1000', displayPrecision: 2,
    contractMonths: ALL_MONTHS, settlementType: 'cash', sessionTemplateId: 'cme_energy_metals' },
  { root: 'MNG', exchange: 'NYMEX', assetClass: 'energy', description: 'Micro Henry Hub Natural Gas',
    tickSize: '0.001', tickValue: '2.50', pointValue: '2500', displayPrecision: 3,
    parentRoot: 'NG', sizeRatio: '0.25',
    contractMonths: ALL_MONTHS, settlementType: 'cash', sessionTemplateId: 'cme_energy_metals' },
  { root: 'QM', exchange: 'NYMEX', assetClass: 'energy', description: 'E-mini Crude Oil',
    tickSize: '0.025', tickValue: '12.50', pointValue: '500', displayPrecision: 3,
    parentRoot: 'CL', sizeRatio: '0.5',
    contractMonths: ALL_MONTHS, settlementType: 'physical', sessionTemplateId: 'cme_energy_metals' },

  // --- metals, the rest ----------------------------------------------------
  { root: 'HG', exchange: 'COMEX', assetClass: 'metal', description: 'Copper',
    tickSize: '0.0005', tickValue: '12.50', pointValue: '25000', displayPrecision: 4,
    contractMonths: ALL_MONTHS, settlementType: 'physical', sessionTemplateId: 'cme_energy_metals' },
  { root: 'MHG', exchange: 'COMEX', assetClass: 'metal', description: 'Micro Copper',
    tickSize: '0.0005', tickValue: '1.25', pointValue: '2500', displayPrecision: 4,
    parentRoot: 'HG', sizeRatio: '0.1',
    contractMonths: ALL_MONTHS, settlementType: 'physical', sessionTemplateId: 'cme_energy_metals' },
  { root: 'SIL', exchange: 'COMEX', assetClass: 'metal', description: 'Micro Silver (1,000 oz)',
    tickSize: '0.005', tickValue: '5.00', pointValue: '1000', displayPrecision: 3,
    parentRoot: 'SI', sizeRatio: '0.2',
    contractMonths: ALL_MONTHS, settlementType: 'physical', sessionTemplateId: 'cme_energy_metals' },
  { root: 'PL', exchange: 'NYMEX', assetClass: 'metal', description: 'Platinum',
    tickSize: '0.1', tickValue: '5.00', pointValue: '50', displayPrecision: 1,
    contractMonths: QUARTERLY, settlementType: 'physical', sessionTemplateId: 'cme_energy_metals' },
  { root: 'PA', exchange: 'NYMEX', assetClass: 'metal', description: 'Palladium',
    tickSize: '0.05', tickValue: '5.00', pointValue: '100', displayPrecision: 2,
    contractMonths: QUARTERLY, settlementType: 'physical', sessionTemplateId: 'cme_energy_metals' },

  // --- fx futures, the majors ----------------------------------------------
  // Distinct from the spot pairs below: these are exchange-listed contracts
  // with a tick identity that holds, and a trader on a futures broker reaches
  // for 6B rather than GBPUSD.
  { root: '6A', exchange: 'CME', assetClass: 'fx', description: 'Australian Dollar',
    tickSize: '0.00005', tickValue: '5.00', pointValue: '100000', displayPrecision: 5,
    contractMonths: QUARTERLY, settlementType: 'physical', sessionTemplateId: 'cme_fx' },
  { root: '6B', exchange: 'CME', assetClass: 'fx', description: 'British Pound',
    tickSize: '0.0001', tickValue: '6.25', pointValue: '62500', displayPrecision: 4,
    contractMonths: QUARTERLY, settlementType: 'physical', sessionTemplateId: 'cme_fx' },
  { root: '6C', exchange: 'CME', assetClass: 'fx', description: 'Canadian Dollar',
    tickSize: '0.00005', tickValue: '5.00', pointValue: '100000', displayPrecision: 5,
    contractMonths: QUARTERLY, settlementType: 'physical', sessionTemplateId: 'cme_fx' },
  { root: '6S', exchange: 'CME', assetClass: 'fx', description: 'Swiss Franc',
    tickSize: '0.0001', tickValue: '12.50', pointValue: '125000', displayPrecision: 4,
    contractMonths: QUARTERLY, settlementType: 'physical', sessionTemplateId: 'cme_fx' },
  { root: '6N', exchange: 'CME', assetClass: 'fx', description: 'New Zealand Dollar',
    tickSize: '0.0001', tickValue: '10.00', pointValue: '100000', displayPrecision: 4,
    contractMonths: QUARTERLY, settlementType: 'physical', sessionTemplateId: 'cme_fx' },
  { root: '6M', exchange: 'CME', assetClass: 'fx', description: 'Mexican Peso',
    tickSize: '0.00001', tickValue: '5.00', pointValue: '500000', displayPrecision: 5,
    contractMonths: QUARTERLY, settlementType: 'physical', sessionTemplateId: 'cme_fx' },
  { root: 'M6E', exchange: 'CME', assetClass: 'fx', description: 'Micro Euro',
    tickSize: '0.0001', tickValue: '1.25', pointValue: '12500', displayPrecision: 4,
    parentRoot: '6E', sizeRatio: '0.1',
    contractMonths: QUARTERLY, settlementType: 'cash', sessionTemplateId: 'cme_fx' },
  { root: 'M6A', exchange: 'CME', assetClass: 'fx', description: 'Micro Australian Dollar',
    tickSize: '0.0001', tickValue: '1.00', pointValue: '10000', displayPrecision: 4,
    parentRoot: '6A', sizeRatio: '0.1',
    contractMonths: QUARTERLY, settlementType: 'cash', sessionTemplateId: 'cme_fx' },

  // --- grains and oilseeds -------------------------------------------------
  // Quoted in CENTS per bushel, which is why displayPrecision is 2 on a number
  // that looks like 425'4 elsewhere. AEGIS shows decimal cents; the eighths
  // notation is a broker convention, not a property of the contract.
  { root: 'ZC', exchange: 'CBOT', assetClass: 'agricultural', description: 'Corn',
    tickSize: '0.25', tickValue: '12.50', pointValue: '50', displayPrecision: 2,
    contractMonths: ['H', 'K', 'N', 'U', 'Z'], settlementType: 'physical',
    sessionTemplateId: 'cbot_grains' },
  { root: 'ZS', exchange: 'CBOT', assetClass: 'agricultural', description: 'Soybeans',
    tickSize: '0.25', tickValue: '12.50', pointValue: '50', displayPrecision: 2,
    contractMonths: ['F', 'H', 'K', 'N', 'Q', 'U', 'X'], settlementType: 'physical',
    sessionTemplateId: 'cbot_grains' },
  { root: 'ZW', exchange: 'CBOT', assetClass: 'agricultural', description: 'Chicago Wheat',
    tickSize: '0.25', tickValue: '12.50', pointValue: '50', displayPrecision: 2,
    contractMonths: ['H', 'K', 'N', 'U', 'Z'], settlementType: 'physical',
    sessionTemplateId: 'cbot_grains' },
  { root: 'ZL', exchange: 'CBOT', assetClass: 'agricultural', description: 'Soybean Oil',
    tickSize: '0.01', tickValue: '6.00', pointValue: '600', displayPrecision: 2,
    contractMonths: ['F', 'H', 'K', 'N', 'Q', 'U', 'V', 'Z'], settlementType: 'physical',
    sessionTemplateId: 'cbot_grains' },
  { root: 'ZM', exchange: 'CBOT', assetClass: 'agricultural', description: 'Soybean Meal',
    tickSize: '0.1', tickValue: '10.00', pointValue: '100', displayPrecision: 1,
    contractMonths: ['F', 'H', 'K', 'N', 'Q', 'U', 'V', 'Z'], settlementType: 'physical',
    sessionTemplateId: 'cbot_grains' },

  // --- livestock -----------------------------------------------------------
  { root: 'LE', exchange: 'CME', assetClass: 'agricultural', description: 'Live Cattle',
    tickSize: '0.025', tickValue: '10.00', pointValue: '400', displayPrecision: 3,
    contractMonths: ['G', 'J', 'M', 'Q', 'V', 'Z'], settlementType: 'physical',
    sessionTemplateId: 'cme_livestock' },
  { root: 'HE', exchange: 'CME', assetClass: 'agricultural', description: 'Lean Hogs',
    tickSize: '0.025', tickValue: '10.00', pointValue: '400', displayPrecision: 3,
    contractMonths: ['G', 'J', 'K', 'M', 'N', 'Q', 'V', 'Z'], settlementType: 'cash',
    sessionTemplateId: 'cme_livestock' },
  { root: 'GF', exchange: 'CME', assetClass: 'agricultural', description: 'Feeder Cattle',
    tickSize: '0.025', tickValue: '12.50', pointValue: '500', displayPrecision: 3,
    contractMonths: ['F', 'H', 'J', 'K', 'Q', 'U', 'V', 'X'], settlementType: 'cash',
    sessionTemplateId: 'cme_livestock' },

  // --- softs ---------------------------------------------------------------
  { root: 'KC', exchange: 'ICEUS', assetClass: 'agricultural', description: 'Coffee C',
    tickSize: '0.05', tickValue: '18.75', pointValue: '375', displayPrecision: 2,
    contractMonths: ['H', 'K', 'N', 'U', 'Z'], settlementType: 'physical',
    sessionTemplateId: 'ice_softs' },
  { root: 'SB', exchange: 'ICEUS', assetClass: 'agricultural', description: 'Sugar No. 11',
    tickSize: '0.01', tickValue: '11.20', pointValue: '1120', displayPrecision: 2,
    contractMonths: ['H', 'K', 'N', 'V'], settlementType: 'physical',
    sessionTemplateId: 'ice_softs' },
  { root: 'CC', exchange: 'ICEUS', assetClass: 'agricultural', description: 'Cocoa',
    tickSize: '1', tickValue: '10.00', pointValue: '10', displayPrecision: 0,
    contractMonths: ['H', 'K', 'N', 'U', 'Z'], settlementType: 'physical',
    sessionTemplateId: 'ice_softs' },
  { root: 'CT', exchange: 'ICEUS', assetClass: 'agricultural', description: 'Cotton No. 2',
    tickSize: '0.01', tickValue: '5.00', pointValue: '500', displayPrecision: 2,
    contractMonths: ['H', 'K', 'N', 'V', 'Z'], settlementType: 'physical',
    sessionTemplateId: 'ice_softs' },

  // --- crypto futures ------------------------------------------------------
  // The exchange-listed contracts, distinct from the spot pairs below. A prop
  // trader reaches for MBT because their firm permits it; the spot pair is a
  // different venue with different licensing.
  { root: 'BTC', exchange: 'CME', assetClass: 'crypto', description: 'Bitcoin Futures (5 BTC)',
    tickSize: '5', tickValue: '25.00', pointValue: '5', displayPrecision: 0,
    contractMonths: ALL_MONTHS, settlementType: 'cash', sessionTemplateId: 'cme_equity_index' },
  { root: 'MBT', exchange: 'CME', assetClass: 'crypto', description: 'Micro Bitcoin Futures (0.1 BTC)',
    tickSize: '5', tickValue: '0.50', pointValue: '0.1', displayPrecision: 0,
    parentRoot: 'BTC', sizeRatio: '0.02',
    contractMonths: ALL_MONTHS, settlementType: 'cash', sessionTemplateId: 'cme_equity_index' },
  { root: 'ETH', exchange: 'CME', assetClass: 'crypto', description: 'Ether Futures (50 ETH)',
    tickSize: '0.5', tickValue: '25.00', pointValue: '50', displayPrecision: 2,
    contractMonths: ALL_MONTHS, settlementType: 'cash', sessionTemplateId: 'cme_equity_index' },
  { root: 'MET', exchange: 'CME', assetClass: 'crypto', description: 'Micro Ether Futures (0.1 ETH)',
    tickSize: '0.5', tickValue: '0.05', pointValue: '0.1', displayPrecision: 2,
    parentRoot: 'ETH', sizeRatio: '0.002',
    contractMonths: ALL_MONTHS, settlementType: 'cash', sessionTemplateId: 'cme_equity_index' },

  // --- volatility ----------------------------------------------------------
  { root: 'VX', exchange: 'CFE', assetClass: 'equity_index', description: 'Cboe Volatility Index',
    tickSize: '0.05', tickValue: '50.00', pointValue: '1000', displayPrecision: 2,
    contractMonths: ALL_MONTHS, settlementType: 'cash', sessionTemplateId: 'cfe_volatility' },

  // --- index, the rest -----------------------------------------------------
  { root: 'EMD', exchange: 'CME', assetClass: 'equity_index', description: 'E-mini S&P MidCap 400',
    tickSize: '0.1', tickValue: '10.00', pointValue: '100', displayPrecision: 2,
    contractMonths: QUARTERLY, settlementType: 'cash', sessionTemplateId: 'cme_equity_index' },
  { root: 'NKD', exchange: 'CME', assetClass: 'equity_index', description: 'Nikkei 225 (USD)',
    tickSize: '5', tickValue: '25.00', pointValue: '5', displayPrecision: 0,
    contractMonths: QUARTERLY, settlementType: 'cash', sessionTemplateId: 'cme_equity_index' },

  // --- spot forex ----------------------------------------------------------
  // The kind the tick identity does NOT describe, and the registry exempts.
  // pointValue is 0 here on purpose: a pip's cash value is not a property of
  // the pair, it is a function of lot size and of the ACCOUNT's currency, and
  // it is resolved at fill time in Phase 44. Writing a plausible number here
  // would be inventing one.
  // Spot FX is generated from the venue's own metadata rather than written
  // here — 68 pairs typed by hand is 68 chances to transpose a decimal, and a
  // wrong tickSize silently scales every pip and R-multiple on that pair.
  // `pnpm fx:registry` rewrites it. See fx.gen.ts.
  ...FX_PAIRS,
];

function build(s: Spec): Instrument {
  const tickSize = dec(s.tickSize);
  const tickValue = dec(s.tickValue);
  const pointValue = dec(s.pointValue);

  const kind: InstrumentKind = s.kind ?? 'future';

  // A venue must be a row in VENUES. Same bargain as the tick identity below:
  // fail at module load rather than carry an unknown venue into an entitlement
  // decision, where "not licensed" is the dangerous default.
  if (!isKnownVenue(s.exchange)) {
    throw new Error(`Instrument ${s.root}: unknown venue "${s.exchange}". Add a row to VENUES.`);
  }

  // Same bargain again. A session template decides where the trading day is
  // cut, and an unresolvable id does not fail here — it throws later, inside
  // whichever request first asks for the timezone, which is how three invented
  // template ids took down /v1/instruments rather than failing at startup.
  if (!Object.hasOwn(SESSION_TEMPLATES, s.sessionTemplateId)) {
    throw new Error(
      `Instrument ${s.root}: unknown session template "${s.sessionTemplateId}". `
      + `Add it to SESSION_TEMPLATES.`,
    );
  }

  // An FX row without these cannot be valued at all — `pointValue` is zero for
  // FX by design, so a missing contractSize does not degrade the number, it
  // makes every P&L on that pair exactly zero.
  if (kind === 'fx_spot' && (!s.base || !s.quote || !s.contractSize)) {
    throw new Error(
      `Instrument ${s.root}: spot FX needs base, quote and contractSize. `
      + `Without them every P&L on this pair computes as zero.`,
    );
  }

  // The identity that catches transcription errors before they reach a P&L.
  //
  // It holds wherever a "point" has a fixed cash value — futures, shares,
  // crypto — and does NOT hold for spot FX, where a pip is worth an amount that
  // depends on lot size and on the account's own currency, resolved at fill
  // time against a rate. Asserting it there would force a fabricated
  // pointValue, so FX is exempted rather than made to lie. Phase 44 owns that
  // arithmetic; this is only the guard that stops a typo reaching a P&L.
  if (kind !== 'fx_spot' && kind !== 'cfd') {
    const derived = tickSize.times(pointValue);
    if (!derived.eq(tickValue)) {
      throw new Error(
        `Instrument ${s.root}: tickValue (${tickValue.toFixed()}) != tickSize * pointValue ` +
          `(${tickSize.toFixed()} * ${pointValue.toFixed()} = ${derived.toFixed()})`,
      );
    }
  }

  const inst: Instrument = {
    canonicalSymbol: `${s.exchange}:${s.root}`,
    root: s.root,
    kind,
    exchange: s.exchange,
    assetClass: s.assetClass,
    description: s.description,
    tickSize,
    tickValue,
    pointValue,
    currency: s.currency ?? 'USD',
    priceFormat: s.priceFormat ?? 'decimal',
    displayPrecision: s.displayPrecision,
    quantityStep: dec(s.quantityStep ?? '1'),
    base: s.base,
    quote: s.quote,
    contractSize: s.contractSize === undefined ? undefined : dec(s.contractSize),
    contractMonths: s.contractMonths,
    settlementType: s.settlementType,
    // Only futures roll, so only futures get a default. A share with a
    // roll rule is a field someone will eventually branch on.
    rollRule: kind === 'future' ? (s.rollRule ?? 'volume') : s.rollRule,
    sessionTemplateId: s.sessionTemplateId,
    specVersion: 1,
  };
  return s.parentRoot
    ? { ...inst, parentRoot: s.parentRoot, sizeRatio: dec(s.sizeRatio ?? '0.1') }
    : inst;
}

const BY_ROOT: ReadonlyMap<string, Instrument> = new Map(
  SPECS.map((s) => [s.root, build(s)]),
);

/** Every instrument in the master, in registry order. */
export const INSTRUMENTS: readonly Instrument[] = [...BY_ROOT.values()];

/** Look up by root (`ES`) or canonical symbol (`CME:ES`, `CME:ES:2026-12`). */
export function findInstrument(symbol: string): Instrument | undefined {
  const direct = BY_ROOT.get(symbol);
  if (direct) return direct;
  const parts = symbol.split(':');
  // `CME:ES` -> ES ; `CME:ES:2026-12` -> ES
  return parts.length >= 2 ? BY_ROOT.get(parts[1] ?? '') : undefined;
}

/**
 * The session template to file a symbol's trades against, with a fallback.
 *
 * ## Why a fallback exists at all
 *
 * Everywhere else in this codebase an unknown instrument is a programming
 * error, and `getInstrument` throwing is correct. This is the one class of
 * caller where it is not: the rule engine and the analytics both walk a whole
 * journal, and a single row carrying a symbol the master does not know would
 * take down the Today tab, the rule gauges and the reports at once — for an
 * account whose other ninety trades are fine.
 *
 * A broker can report a symbol AEGIS has never heard of. A user can hold a
 * contract added to their platform this morning. Neither is a programming
 * error, and neither is a reason to render nothing.
 *
 * ## What the fallback is, and what it is not
 *
 * `cme_equity_index` — the most common futures schedule, and one that puts a
 * trade on the right calendar DAY for anything trading US hours. It is used to
 * answer "which trading day is this trade on", nothing else.
 *
 * It is emphatically NOT a fallback for tick size or point value. There is no
 * safe default for those: a guessed tick value produces a plausible, wrong P&L,
 * which is the failure CLAUDE.md names explicitly. Callers that need money
 * figures still use `findInstrument` and skip what they cannot price.
 */
export const FALLBACK_SESSION_TEMPLATE = 'cme_equity_index';

/**
 * The MARKET a contract belongs to, rather than the contract itself.
 *
 * A micro is the same market as its parent — that is the entire point of a
 * micro — so MES resolves to ES. Anything that reasons about exposure or about
 * a trader's history in an instrument wants this, not `root`: counting MES
 * trades as a different instrument splits one trader's ES history in half, and
 * counting MES positions as a different market lets somebody be long the S&P
 * twice with no screen saying so.
 *
 * Safe on an unknown symbol, which becomes its own market. Folding it into
 * someone else's would be worse than isolating it.
 */
export function marketRootOf(symbol: string): string {
  const i = findInstrument(symbol);
  if (!i) return symbol;
  return i.parentRoot ?? i.root;
}

export function sessionTemplateIdFor(symbol: string): string {
  return findInstrument(symbol)?.sessionTemplateId ?? FALLBACK_SESSION_TEMPLATE;
}

/** Look up, or throw. Use where a missing instrument is a programming error. */
export function getInstrument(symbol: string): Instrument {
  const i = findInstrument(symbol);
  if (!i) throw new Error(`Unknown instrument: ${symbol}`);
  return i;
}

/**
 * The root whose analysis this instrument uses.
 *
 * Micros inherit their parent's broadcast analysis — MES tracks ES, the levels
 * and structure are identical, and only tick value differs. Analysing them
 * separately would be paying twice for the same answer.
 *
 * @see docs/adr/0006-ai-broadcast-architecture.md
 */
export function analysisRoot(symbol: string): string {
  const i = getInstrument(symbol);
  return i.parentRoot ?? i.root;
}

/**
 * A dated contract's ticker: root, month letter, one or two year digits.
 *
 * The tail does the work, not the root pattern. It is anchored, and it must be
 * a real futures month letter followed by one or two digits — so `ZBZ5` can
 * only split as ZB + Z5, never ZBZ + 5, and `MESA5` does not resolve at all
 * because A is not a month. Chopping a fixed number of trailing characters
 * would get both wrong.
 *
 * `[A-Z0-9]` rather than `[A-Z]` on the root because the FX contracts start
 * with a digit: 6E, 6J, 6A.
 */
const CONTRACT_TICKER = /^([A-Z0-9]+)([FGHJKMNQUVXZ])(\d{1,2})$/;

/**
 * The root a reading would be published under, for any symbol at all.
 *
 * ## Why this exists next to `analysisRoot`
 *
 * `analysisRoot` throws on anything the master does not know, which is right
 * for the places that call it — a chart asked to render an instrument that
 * does not exist is a programming error. It is wrong for the two places that
 * hand it whatever a BROKER said.
 *
 * A broker reports the contract, not the root: Rithmic answers `ESZ5`, not
 * `ES`. Nothing between the adapter and the widget turned one into the other,
 * so a trader with a live position had `ESZ5` written into their held-symbol
 * index — where `usersHolding('ES')` could never match it — and handed to
 * `analysis.get`, which threw and took the whole widget endpoint down with it.
 *
 * ## What it does
 *
 * Three steps, in order, and it never throws:
 *
 *   1. Known symbol, including `CME:ES` and `CME:ES:2026-12` — resolve it and
 *      follow `parentRoot`, so MES answers ES.
 *   2. Dated ticker — strip the month and year and resolve what is left, so
 *      `MESZ5` also answers ES.
 *   3. Anything else — `null`. A contract added to the trader's platform this
 *      morning is not a programming error and is not a reason to render
 *      nothing; the caller drops it and carries on.
 *
 * The distinction between step 3 and a throw is the whole point. See the
 * fallback note on `sessionTemplateIdFor`, which draws the same line for the
 * same reason.
 */
export function readRootOf(symbol: string): string | null {
  const s = symbol.trim().toUpperCase();
  if (s === '') return null;

  const direct = findInstrument(s);
  if (direct) return direct.parentRoot ?? direct.root;

  const m = CONTRACT_TICKER.exec(s);
  const dated = m ? findInstrument(m[1] ?? '') : undefined;
  return dated ? dated.parentRoot ?? dated.root : null;
}

/**
 * Every root whose readings are published under this one, including itself.
 *
 * The inverse of `analysisRoot`, and the fan-out needs it because the two
 * sides of a wake speak different vocabularies. A reading is published under
 * the parent — `ES` — while a watchlist holds what the trader chose, which for
 * a micro trader is `MES`. Matching the published root against watchlist rows
 * directly finds nobody, and the failure is silent: their widget renders the
 * ES reading perfectly and simply never refreshes on its own.
 *
 * Expanded at query time rather than normalised on write, because a watchlist
 * is the trader's own list and rewriting `MES` to `ES` in it would change what
 * they see to fix what they do not.
 */
export function rootsAnalysingAs(root: string): readonly string[] {
  const want = root.trim().toUpperCase();
  return INSTRUMENTS
    .filter((i) => (i.parentRoot ?? i.root) === want)
    .map((i) => i.root);
}

/** The 12 parent contracts the broadcast scheduler analyses. */
export const ANALYSIS_UNIVERSE: readonly string[] =
  ['ES', 'NQ', 'YM', 'RTY', 'CL', 'NG', 'GC', 'SI', 'ZB', 'ZN', '6E', '6J'];

/** Physically-delivered contracts, where holding past first notice is a hazard. */
export function isPhysicallyDelivered(symbol: string): boolean {
  return getInstrument(symbol).settlementType === 'physical';
}
